import dayjs, { Dayjs } from "dayjs"
import "dayjs/locale/en-gb"
import "dayjs/locale/sv"
import calendar from "dayjs/plugin/calendar"

import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"
import isoWeek from "dayjs/plugin/isoWeek"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import weekOfYear from "dayjs/plugin/weekOfYear"

import { Language } from "../i18n/language"
import { DateKey } from "./DateKey"
import { JuloUtils } from "./JuloUtils"
import { WeekDay } from "./Weekday"
import { WeekdayFresh } from "./WeekdayFresh"

dayjs.locale("sv")
dayjs.extend(weekOfYear)
dayjs.extend(isoWeek)
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(calendar)
dayjs.extend(utc)
dayjs.extend(timezone)

const WEEKDAY_OFFSETS: Record<WeekdayFresh | WeekDay, number> = {
  [WeekdayFresh.MONDAY]: 0,
  [WeekdayFresh.TUESDAY]: 1,
  [WeekdayFresh.WEDNESDAY]: 2,
  [WeekdayFresh.THURSDAY]: 3,
  [WeekdayFresh.FRIDAY]: 4,
  [WeekdayFresh.SATURDAY]: 5,
  [WeekdayFresh.SUNDAY]: 6,
  [WeekDay.MONDAY]: 0,
  [WeekDay.TUESDAY]: 1,
  [WeekDay.WEDNESDAY]: 2,
  [WeekDay.THURSDAY]: 3,
  [WeekDay.FRIDAY]: 4,
  [WeekDay.SATURDAY]: 5,
  [WeekDay.SUNDAY]: 6,
}

const DAYJS_LOCALES: Record<Language, string> = { sv: "sv", en: "en-gb" }

// Words for the relative-date helpers below. These are date formatting, like
// dayjs' own month names, so they live with the formatter rather than in the
// translation tables. `n` picks singular/plural.
const DATE_WORDS: Record<
  Language,
  {
    today: string
    yesterday: string
    tomorrow: string
    lastWeekday: string
    nextWeekday: string
    minutes: (n: number) => string
    hours: (n: number) => string
    days: (n: number) => string
    weeks: (n: number) => string
    months: (n: number) => string
    years: (n: number) => string
    inFuture: (amount: string) => string
    ago: (amount: string) => string
  }
> = {
  sv: {
    today: "Idag",
    yesterday: "Igår",
    tomorrow: "Imorgon",
    lastWeekday: "[I] dddd[s]",
    nextWeekday: "[Nu på] dddd",
    minutes: (n) => `${n} ${n === 1 ? "minut" : "minuter"}`,
    hours: (n) => `${n} ${n === 1 ? "timme" : "timmar"}`,
    days: (n) => `${n} ${n === 1 ? "dag" : "dagar"}`,
    weeks: (n) => `${n} ${n === 1 ? "vecka" : "veckor"}`,
    months: (n) => `${n} ${n === 1 ? "månad" : "månader"}`,
    years: (n) => `${n} år`,
    inFuture: (amount) => `om ${amount}`,
    ago: (amount) => `${amount} sen`,
  },
  en: {
    today: "Today",
    yesterday: "Yesterday",
    tomorrow: "Tomorrow",
    lastWeekday: "[Last] dddd",
    nextWeekday: "[This] dddd",
    minutes: (n) => `${n} ${n === 1 ? "minute" : "minutes"}`,
    hours: (n) => `${n} ${n === 1 ? "hour" : "hours"}`,
    days: (n) => `${n} ${n === 1 ? "day" : "days"}`,
    weeks: (n) => `${n} ${n === 1 ? "week" : "weeks"}`,
    months: (n) => `${n} ${n === 1 ? "month" : "months"}`,
    years: (n) => `${n} ${n === 1 ? "year" : "years"}`,
    inFuture: (amount) => `in ${amount}`,
    ago: (amount) => `${amount} ago`,
  },
}

class DateUtilsClass {
  private static instance: DateUtilsClass
  // Swedish unless an app switches it; the API never does, so everything it
  // formats for customers stays Swedish.
  private language: Language = "sv"

  private constructor() {}

  setLanguage(language: Language): void {
    this.language = language
    dayjs.locale(DAYJS_LOCALES[language])
  }

  private get locale(): string {
    return DAYJS_LOCALES[this.language]
  }

  private get words() {
    return DATE_WORDS[this.language]
  }

  public static getInstance(): DateUtilsClass {
    if (!DateUtilsClass.instance) {
      DateUtilsClass.instance = new DateUtilsClass()
    }

    return DateUtilsClass.instance
  }

  private static languageInstances = new Map<Language, DateUtilsClass>()

  // A formatter fixed to one language, for apps that render several languages
  // at once (julo's server renders / and /en side by side) and so can't
  // switch the shared instance with setLanguage().
  public static forLanguage(language: Language): DateUtilsClass {
    const existing = DateUtilsClass.languageInstances.get(language)
    if (existing) return existing

    const instance = new DateUtilsClass()
    instance.language = language
    DateUtilsClass.languageInstances.set(language, instance)
    return instance
  }

  prettyPrintHours(hours: number): string {
    const numDays = Math.floor(hours / 24)
    const remainingHours = hours % 24

    if (numDays === 0) {
      return `${hours} ${hours === 1 ? "timme" : "timmar"}`
    }
    if (remainingHours === 0) {
      return `${numDays} ${numDays === 1 ? "dag" : "dagar"}`
    }
    return `${numDays} ${numDays === 1 ? "dag" : "dagar"} och ${remainingHours} ${remainingHours === 1 ? "timme" : "timmar"}`
  }

  getWeekDay(date: Date): number {
    const dayIndex = dayjs(date).tz("Europe/Stockholm").day()

    // Convert sunday to 6 (last index in array)
    return dayIndex === 0 ? 6 : dayIndex - 1
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return dayjs(date1)
      .tz("Europe/Stockholm")
      .isSame(dayjs(date2).tz("Europe/Stockholm"), "day")
  }

  formatDateAndTime(date: Date | Dayjs): string {
    return dayjs(date).format("YYYY-MM-DD [kl.] HH:mm")
  }

  formatTime(date: Date | Dayjs): string {
    return dayjs(date).format("HH:mm")
  }

  formatDate(
    date: Date | Dayjs,
    options: {
      monthStyle?: "short" | "full"
      capitalize?: boolean
    } = {},
  ) {
    const { monthStyle = "short", capitalize = true } = options
    const stockholmDate = dayjs(date).tz("Europe/Stockholm").locale(this.locale)
    const monthToken = monthStyle === "full" ? "MMMM" : "MMM"
    const isOtherYear =
      stockholmDate.year() !== dayjs().tz("Europe/Stockholm").year()
    const yearSuffix = isOtherYear ? " YYYY" : ""

    const dayjsDate = stockholmDate.calendar(null, {
      lastDay: `[${this.words.yesterday}], D ${monthToken}${yearSuffix}`,
      sameDay: `[${this.words.today}], D ${monthToken}${yearSuffix}`,
      nextDay: `[${this.words.tomorrow}], D ${monthToken}${yearSuffix}`,
      lastWeek: `${this.words.lastWeekday}, D ${monthToken}${yearSuffix}`,
      nextWeek: `dddd, D ${monthToken}${yearSuffix}`,
      sameElse: `dddd, D ${monthToken}${yearSuffix}`,
    })

    return capitalize
      ? JuloUtils.capitalize(dayjsDate)
      : dayjsDate.toLowerCase()
  }

  formatDateSimple(date: Date | Dayjs) {
    const dayjsDate = dayjs(date)
      .locale(this.locale)
      .calendar(null, {
        lastDay: `[${this.words.yesterday}]`,
        sameDay: `[${this.words.today}]`,
        lastWeek: this.words.lastWeekday.replace("[s]", ""),
        nextWeek: `${this.words.nextWeekday} (D MMMM)`,
        sameElse: "D MMMM YYYY",
      })

    return JuloUtils.capitalize(dayjsDate)
  }

  withUtcDiffAppended(date: Date): Date {
    // Use when only date (without time) is stored in database
    const _date = dayjs(date)
    const diffInMinutes = _date.utcOffset()
    const dateWithUtcDiffAppended = _date.add(diffInMinutes, "minutes")
    return dateWithUtcDiffAppended.toDate()
  }

  getDatesForWeek(week: number, year: number): Date[] {
    const dates: Dayjs[] = []

    const startOfWeek = dayjs().year(year).isoWeek(week).startOf("isoWeek")

    for (let i = 0; i < 7; i++) {
      dates.push(dayjs(startOfWeek).add(i, "days"))
    }

    return dates.map((date) => date.toDate())
  }

  getDatesBetween(startDate: Date, endDate: Date): Date[] {
    const dates = []
    let currentDate = startDate
    currentDate.setHours(12)

    while (currentDate <= endDate) {
      dates.push(currentDate)

      currentDate = dayjs(currentDate).add(1, "days").toDate()
    }

    return dates
  }

  addDays(date: Date, days: number): Date {
    return dayjs(date).add(days, "days").toDate()
  }

  getStartOfWeek(date: Date): Date {
    return dayjs(date).startOf("isoWeek").toDate()
  }

  getStartOfDay(date: Date): Date {
    return dayjs(date).tz("Europe/Stockholm").startOf("day").toDate()
  }

  getEndOfDay(date: Date): Date {
    return dayjs(date).tz("Europe/Stockholm").endOf("day").toDate()
  }

  /**
   * Converts a date to UTC midnight of the Stockholm calendar date.
   * Use for querying @db.Date columns in PostgreSQL, which store dates
   * as UTC midnight timestamps.
   */
  toDbDate(date: Date): Date {
    const stockholmDay = dayjs(date).tz("Europe/Stockholm").format("YYYY-MM-DD")
    return new Date(`${stockholmDay}T00:00:00.000Z`)
  }

  /**
   * Returns the number of calendar days between two dates in Stockholm timezone.
   * Positive if `to` is after `from`, negative if before.
   */
  diffInDays(from: Date, to: Date): number {
    const fromDay = dayjs(from).tz("Europe/Stockholm").startOf("day")
    const toDay = dayjs(to).tz("Europe/Stockholm").startOf("day")
    return toDay.diff(fromDay, "day")
  }

  getEndOfWeek(date: Date): Date {
    return dayjs(date).endOf("isoWeek").toDate()
  }

  getDateKey(date: Date): DateKey {
    return dayjs(date).tz("Europe/Stockholm").format("YYYY-MM-DD") as DateKey
  }

  /**
   * Converts a DateKey string (YYYY-MM-DD) to a Date object at midday UTC.
   * This avoids timezone boundary issues that occur when creating dates at midnight.
   * Using UTC ensures consistent behavior across different server timezones.
   */
  dateKeyToDate(dateKey: DateKey): Date {
    return new Date(`${dateKey}T12:00:00.000Z`)
  }

  getDateWithMiddayTime(date: Date): Date {
    // Parse the date in Sweden's timezone to get the correct date components
    const d = dayjs(date).tz("Europe/Stockholm")
    return dayjs()
      .year(d.year())
      .month(d.month())
      .date(d.date())
      .hour(12)
      .minute(0)
      .second(0)
      .millisecond(0)
      .toDate()
  }

  getWeekDayEnum(weekday: WeekdayFresh): WeekDay {
    switch (weekday) {
      case WeekdayFresh.MONDAY:
        return WeekDay.MONDAY
      case WeekdayFresh.TUESDAY:
        return WeekDay.TUESDAY
      case WeekdayFresh.WEDNESDAY:
        return WeekDay.WEDNESDAY
      case WeekdayFresh.THURSDAY:
        return WeekDay.THURSDAY
      case WeekdayFresh.FRIDAY:
        return WeekDay.FRIDAY
      case WeekdayFresh.SATURDAY:
        return WeekDay.SATURDAY
      case WeekdayFresh.SUNDAY:
        return WeekDay.SUNDAY
    }
  }

  getWeekdayLabel(weekday: WeekdayFresh | WeekDay): string {
    // 2024-01-01 is a Monday, so offsets 0–6 give Monday–Sunday
    const offset = WEEKDAY_OFFSETS[weekday]
    const name = dayjs("2024-01-01").add(offset, "day").locale(this.locale)
    return JuloUtils.capitalize(name.format("dddd"))
  }

  /**
   * Formats a date like "29:e april" (Swedish ordinal format with month name)
   */
  formatDayAndMonth(date: Date | Dayjs): string {
    const d = dayjs(date).tz("Europe/Stockholm").locale(this.locale)
    const day = d.date()
    const month = d.format("MMMM")
    if (this.language === "en") return `${day} ${month}`

    // Swedish ordinal suffix: 1:a, 2:a, 21:a, 22:a, 31:a use ":a"
    // All others (including 11, 12) use ":e"
    const lastDigit = day % 10
    const isElevenOrTwelve = day === 11 || day === 12
    const suffix =
      (lastDigit === 1 || lastDigit === 2) && !isElevenOrTwelve ? ":a" : ":e"

    return `${day}${suffix} ${month}`
  }

  /**
   * Formats a date like "7 maj" (day + short month in Swedish)
   */
  prettyPrintDate(date: Date | Dayjs): string {
    const d = dayjs(date).tz("Europe/Stockholm").locale(this.locale)
    return d.format("D MMM")
  }

  /**
   * Returns the relative time suffix for a date in Swedish.
   * Future: (om X minuter), (om X timmar), (imorgon), or null for 48+ hours
   * Past: (X minuter sen), (X timmar sen), (igår), (X dagar sen), (X veckor sen), (X månader sen), (X år sen)
   */
  getRelativeTimeSuffix(date: Date | Dayjs, compact?: boolean): string | null {
    const now = dayjs().tz("Europe/Stockholm")
    const target = dayjs(date).tz("Europe/Stockholm")
    const diffMinutes = target.diff(now, "minute")
    const diffHours = target.diff(now, "hour")
    const diffDays = target.startOf("day").diff(now.startOf("day"), "day")

    const isToday = target.isSame(now, "day")
    const isTomorrow = target.isSame(now.add(1, "day"), "day")
    const isYesterday = target.isSame(now.subtract(1, "day"), "day")

    // Future dates
    if (diffMinutes > 0) {
      if (diffMinutes <= 60) {
        if (compact) {
          return this.words.inFuture(`${diffMinutes}m`)
        }
        return this.words.inFuture(this.words.minutes(diffMinutes))
      }
      if (diffHours <= 24 && isToday) {
        if (compact) {
          return this.words.inFuture(`${diffHours}h`)
        }
        return this.words.inFuture(this.words.hours(diffHours))
      }
      if (isTomorrow) {
        return this.words.tomorrow.toLowerCase()
      }
      // 48+ hours in the future - no suffix
      return null
    }

    // Past dates
    const absDiffMinutes = Math.abs(diffMinutes)
    const absDiffHours = Math.abs(diffHours)
    const absDiffDays = Math.abs(diffDays)

    if (absDiffMinutes < 60) {
      if (compact) {
        return `${absDiffMinutes}m`
      }
      return this.words.ago(this.words.minutes(absDiffMinutes))
    }

    if (isYesterday) {
      return this.words.yesterday.toLowerCase()
    }

    if (absDiffHours < 24 && isToday) {
      if (compact) {
        return `${absDiffHours}h`
      }
      return this.words.ago(this.words.hours(absDiffHours))
    }

    if (compact) {
      return `${absDiffDays}d`
    }

    if (absDiffDays < 7) {
      return this.words.ago(this.words.days(absDiffDays))
    }

    const absDiffWeeks = Math.floor(absDiffDays / 7)
    if (absDiffDays < 28) {
      return this.words.ago(this.words.weeks(absDiffWeeks))
    }

    const absDiffMonths = Math.abs(target.diff(now, "month"))
    if (absDiffMonths < 12) {
      return this.words.ago(this.words.months(absDiffMonths))
    }

    const absDiffYears = Math.abs(target.diff(now, "year"))
    if (absDiffYears < 2) {
      return this.words.ago(this.words.years(1))
    }

    return this.words.ago(this.words.years(absDiffYears))
  }

  /**
   * Formats a date like "7 maj (3 veckor sen)" with relative time suffix
   */
  prettyPrintDateWithRelative(date: Date | Dayjs): string {
    const prettyDate = this.prettyPrintDate(date)
    const relativeSuffix = this.getRelativeTimeSuffix(date)

    if (relativeSuffix) {
      return `${prettyDate} (${relativeSuffix})`
    }

    return prettyDate
  }

  /**
   * Checks if the current time in Swedish timezone is past the given cutoff time.
   * @param cutoffTime - Time string in "HH:mm" format (e.g., "14:00")
   * @returns true if current Swedish time is past the cutoff time
   */
  isTimePastCutoff(cutoffTime: string): boolean {
    const parts = cutoffTime.split(":").map(Number)
    const hours = parts[0] ?? 0
    const minutes = parts[1] ?? 0
    const nowSwedish = dayjs().tz("Europe/Stockholm")
    const cutoff = nowSwedish
      .startOf("day")
      .add(hours, "hour")
      .add(minutes, "minute")
    return nowSwedish.isAfter(cutoff)
  }

  /**
   * Checks if a date is today in Swedish timezone.
   * Useful for date-only fields that come from the database as UTC midnight.
   */
  isTodayInStockholm(date: Date | Dayjs): boolean {
    const nowSwedish = dayjs().tz("Europe/Stockholm")
    const dateSwedish = dayjs(date).tz("Europe/Stockholm")
    return dateSwedish.isSame(nowSwedish, "day")
  }

  /**
   * Checks if a date is in the past in Swedish timezone.
   * Useful for date-only fields that come from the database as UTC midnight.
   */
  isPastInStockholm(date: Date | Dayjs): boolean {
    const nowSwedish = dayjs().tz("Europe/Stockholm").startOf("day")
    const dateSwedish = dayjs(date).tz("Europe/Stockholm").startOf("day")
    return dateSwedish.isBefore(nowSwedish)
  }

  /**
   * Checks if a date is tomorrow in Swedish timezone.
   */
  isTomorrowInStockholm(date: Date | Dayjs): boolean {
    const tomorrowSwedish = dayjs().tz("Europe/Stockholm").add(1, "day")
    const dateSwedish = dayjs(date).tz("Europe/Stockholm")
    return dateSwedish.isSame(tomorrowSwedish, "day")
  }

  /**
   * Checks if a date is yesterday in Swedish timezone.
   */
  isYesterdayInStockholm(date: Date | Dayjs): boolean {
    const yesterdaySwedish = dayjs().tz("Europe/Stockholm").subtract(1, "day")
    const dateSwedish = dayjs(date).tz("Europe/Stockholm")
    return dateSwedish.isSame(yesterdaySwedish, "day")
  }

  /**
   * Checks if a date is in the future in Swedish timezone.
   * Useful for date-only fields that come from the database as UTC midnight.
   */
  isFutureInStockholm(date: Date | Dayjs): boolean {
    return !this.isTodayInStockholm(date) && !this.isPastInStockholm(date)
  }

  /**
   * Returns a Swedish day-bucket label for a past date, used to group
   * recently-changed jobs in the CRM. Mechanics asked for concrete dates
   * past "Igår" — counting back "Sex dagar sen" / "Fyra veckor sen" is
   * harder to read than a weekday + date.
   *   Today      → "Idag"
   *   Yesterday  → "Igår"
   *   Same year  → "Mån 27 apr"
   *   Older      → "27 apr 2025"
   */
  getRelativeDayBucketLabel(date: Date | Dayjs): string {
    const nowStartOfDay = dayjs().tz("Europe/Stockholm").startOf("day")
    const dateStartOfDay = dayjs(date).tz("Europe/Stockholm").startOf("day")
    const dayDiff = nowStartOfDay.diff(dateStartOfDay, "day")

    if (dayDiff <= 0) return this.words.today
    if (dayDiff === 1) return this.words.yesterday

    const sameYear = dateStartOfDay.year() === nowStartOfDay.year()
    if (sameYear) {
      const weekday = dateStartOfDay.locale(this.locale).format("ddd")
      const capitalised = weekday.charAt(0).toUpperCase() + weekday.slice(1)
      return `${capitalised} ${dateStartOfDay.locale(this.locale).format("D MMM")}`
    }

    return dateStartOfDay.locale(this.locale).format("D MMM YYYY")
  }

  /**
   * Calculates the time difference from a deadline in milliseconds.
   * Uses Swedish timezone (Europe/Stockholm) for same-day comparison.
   * @param deadline - The deadline date to compare against
   * @returns undefined if no deadline, 0 if same day and not yet overdue,
   *          positive number if overdue (milliseconds), negative if future (milliseconds)
   */
  calculateTimeDifferenceFromDeadline(
    deadline: Date | null,
  ): number | undefined {
    if (!deadline) return undefined

    const now = dayjs()
    const nowSwedish = dayjs().tz("Europe/Stockholm")
    const deadlineSwedish = dayjs(deadline).tz("Europe/Stockholm")

    // Check if same day in Swedish timezone
    const isSameDay = nowSwedish.isSame(deadlineSwedish, "day")

    // Calculate time difference in milliseconds
    const timeDiff = now.valueOf() - dayjs(deadline).valueOf()

    // If same day and deadline hasn't passed yet, return 0
    if (isSameDay && timeDiff <= 0) return 0

    // Return time difference: positive = overdue, negative = future
    return timeDiff
  }

  /**
   * Returns the number of calendar days from today to the given date in Swedish timezone.
   * Positive = future, negative = past, 0 = today.
   */
  getDaysUntil(date: Date | Dayjs): number {
    const nowSwedish = dayjs().tz("Europe/Stockholm").startOf("day")
    const targetSwedish = dayjs(date).tz("Europe/Stockholm").startOf("day")
    return targetSwedish.diff(nowSwedish, "day")
  }

  /**
   * Formats delivery time as a relative Swedish string.
   * E.g. "idag", "imorgon", "om 5 dagar", "om 3 veckor".
   */
  formatDeliveryTime(date: Date | Dayjs): string {
    const days = this.getDaysUntil(date)

    if (days <= 0) {
      return "idag"
    }
    if (days === 1) {
      return "imorgon"
    }
    if (days < 14) {
      return `om ${days} dagar`
    }
    const weeks = Math.floor(days / 7)
    return `om ${weeks} ${weeks === 1 ? "vecka" : "veckor"}`
  }
}
export const DateUtils = DateUtilsClass.getInstance()
export type LocalizedDateUtils = DateUtilsClass
export const getDateUtils = (language: Language): LocalizedDateUtils =>
  DateUtilsClass.forLanguage(language)
